import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import type { AuthUser } from '@repo/contracts';

import { resetUser, trackEvent } from '../analytics/posthog';
import { signOutEverywhere } from './service';
import { fetchSession, sessionKeys } from './session-api';

/**
 * The signed-in user, from anywhere in the app — no provider/context needed,
 * just this hook. Backed by TanStack Query (the session is server state, same
 * as links), so concurrent callers share one request instead of each firing
 * their own fetch, and it plays by the same rules as the rest of the app's
 * data: query key factory, invalidate-to-refetch, no manual state syncing.
 *
 * staleTime: Infinity — the session doesn't change on its own between visits,
 * only in response to sign-in/sign-out, both of which invalidate this key
 * explicitly (see refreshSession/signOut below) rather than relying on a
 * background refetch.
 */
export function useAuth() {
  const queryClient = useQueryClient();

  const { data, isPending } = useQuery({
    queryFn: fetchSession,
    queryKey: sessionKeys.all,
    staleTime: Infinity,
  });

  const refreshSession = useCallback(
    () => queryClient.invalidateQueries({ queryKey: sessionKeys.all }),
    [queryClient],
  );

  const signOut = useCallback(async () => {
    // Before resetUser below, or the event belongs to nobody.
    trackEvent('signed_out');
    await signOutEverywhere();
    // Before the cache write, so the last thing attributed to this person is
    // their own sign-out. Without a reset the next person on this browser
    // inherits the previous one's identity, which is worse than no analytics.
    resetUser();
    queryClient.setQueryData(sessionKeys.all, {
      authenticated: false,
      user: null,
    });
  }, [queryClient]);

  const user: AuthUser | null = data?.authenticated ? data.user : null;
  /**
   * Finished signup (see Session.activated). Absent reads as activated, so a
   * response without the field never locks an existing user out.
   */
  const activated = !!user && data?.activated !== false;

  return { activated, loading: isPending, refreshSession, signOut, user };
}
